import countries from 'constants/countries';
import countriesFi from 'constants/countries-fi';
import countriesSv from 'constants/countries-sv';
import countriesDe from 'constants/countries-de';
import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js';
import { PERCENT } from 'constants/discount-units';
import * as DOMPurify from 'dompurify';

export const delay = async (timeout = 200, data = null): Promise<any> => {
  await new Promise((resolve) => {
    setTimeout(() => {
      resolve(data);
    }, timeout);
  });
};

/**
 * Get countries
 */
export function getCountries(
  lang: string,
): Array<{ value: string; name: string }> {
  switch (lang) {
    case 'fi':
      return countriesFi;
    case 'de':
      return countriesDe;
    case 'sv':
      return countriesSv;
  }
  return countries;
}

/**
 * Sort countries
 */
export function sortCountries(
  countries: Array<{ value: string; name: string }>,
): Array<{ value: string; name: string }> {
  return countries.sort((a, b) => a.name.localeCompare(b.name));
}

export const currencyFormatter = (
  currency: string,
  trailingZeros: boolean = true,
): any => {
  const defaults = {
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  };

  const withoutTrailingZeros = {
    currency: currency,
    maximumFractionDigits: 2,
    trailingZeroDisplay: 'stripIfInteger',
  };

  return new Intl.NumberFormat(
    'it-IT',
    trailingZeros ? defaults : withoutTrailingZeros,
  );
};

export function padNumber(number: any): any {
  if (number <= 999) {
    number = `00${number}`.slice(-4);
  }
  return number;
}

/**
 * Check that phone number is a valid phone number
 * @param number phone number to be validated
 * @returns true or false
 */
export const isValidLoginPhoneNumber = (number: string): boolean => {
  // Use libphonenumber-js library for phone number validation, also used in server
  return isValidPhoneNumber(number);
};

/**
 * Return phone number without additional zeros
 * @param number for example +3580401234567
 * @returns phone number from which zeros have been removed, for example +358401234567
 */
export const getValidPhoneNumber = (number: string): string => {
  const phoneNumber = parsePhoneNumber(number);
  if (phoneNumber) return phoneNumber.number;
  return '';
};

/**
 * Returns the amount which coupon reduces original price
 * and applies exchange rate to coupon amount if provided.
 *
 * The exchange rate option is useful in situation when getting converted price
 * and exhcange rate from backend and we know only the coupons amount in original
 * currency, so for example in receipt or summary calculations when original price is
 * converted and we want to know the real converted amount that coupon has reduced
 * we must use exactly the same exchange rate that has been used in payment.
 * @param coupon coupon used
 * @param originalPrice number original price
 * @param exchangeRate number exchange rate
 * @returns number reduced amount
 */
export const getReducedAmount = (
  coupon: Coupon,
  originalPrice: number,
  exchangeRate = 1,
): number => {
  if (!coupon || coupon.amount <= 0 || !coupon.discountUnit) return 0;

  const reducedAmount =
    coupon.discountUnit === PERCENT
      ? originalPrice * (coupon.amount / 100)
      : exchangeRate * coupon.amount;

  return reducedAmount <= originalPrice ? reducedAmount : originalPrice;
};

/**
 * Returns the price after coupon is used
 * and applies exchange rate to coupon amount if provided.
 * @param coupon coupon used
 * @param price number original price
 * @param exchangeRate number exchange rate
 * @returns number price after coupon
 */
export const getPriceAfterCoupon = (
  coupon: Coupon,
  price: number,
  exchangeRate = 1,
): number => {
  if (!coupon || coupon.amount <= 0 || !coupon.discountUnit) return price;
  const amountToReduce = getReducedAmount(coupon, price, exchangeRate);

  return price - amountToReduce;
};

/**
 * Checks if object is empty. Empty object {} evaluates to
 * true so it cannot be tested for falsy in conditions
 * like undefined or null can. Use this instead of lodash _.isEmpty()
 * for this purpose.
 * @param obj object to test
 * @returns true if object is empty else false
 */
export const isEmptyObject = (obj: object): boolean => {
  for (var x in obj) {
    return false;
  }
  return true;
};

/**
 * Creates a sanitized dangerouslySetInnerHTML object from the provided HTML content.
 * @param {string} content - The HTML content to sanitize.
 * @param {boolean} linksInNewWindow - Flag to allow/disallow links to open in a new window.
 * @returns {SanitizedHTMLObject} - The sanitized dangerouslySetInnerHTML object.
 */
export const createSanitizedHTMLObject = (
  content: string,
  linksInNewWindow: boolean = false,
): SanitizedHTMLObject => {
  if (linksInNewWindow) {
    DOMPurify.addHook('afterSanitizeAttributes', (node: Element) => {
      if ('target' in node && node.getAttribute('target') !== '_blank') {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer');
      }
    });
  }
  return { __html: DOMPurify.sanitize(content) };
};
